// Pure, deterministic readout measurements — NO METER WITHOUT A MEASUREMENT.
// Every value is counted from real content in a given DOM scope, so a server-
// baked meter equals what the client recomputes from the same DOM (JS-off ==
// JS-on). Two runs over the same scope return identical values.
//
// Parity with app/services/article_stats.rb (the server source of truth):
//   * words    — whitespace-delimited tokens in .prose p / .pullquote p, empties
//                dropped, matching Ruby String#split over the same prose.
//   * codebays — [data-block="code"] + [data-block="diffish"]; the readout
//                renderers emit data-block="code" for code AND code_walkthrough,
//                and data-block="diffish" for code_diff (ArticleStats CODE_BAY_TYPES).
//   * termlines— non-empty .term-lines .tl reveal spans (ArticleStats#term_lines).
//   * readmin  — ceil(words / 220), min 1. Ruby bakes ceil
//                (ArticleStats#read_minutes); the client MUST match, so this uses
//                Math.ceil — not Math.round — or the settled needle would land a
//                minute below the server-printed fallback for many articles.

export const WORDS_PER_MINUTE = 220

export const MEASURES = {
  words: (scope) =>
    Array.from(scope.querySelectorAll(".prose p, .pullquote p")).reduce(
      (total, el) => total + el.textContent.trim().split(/\s+/).filter(Boolean).length,
      0
    ),
  codebays: (scope) =>
    scope.querySelectorAll('[data-block="code"],[data-block="diffish"]').length,
  termlines: (scope) =>
    Array.from(scope.querySelectorAll(".term-lines .tl")).filter((l) => l.textContent.trim()).length,
  blocks: (scope) => scope.querySelectorAll("[data-block]").length,
  readmin: (scope) => Math.max(1, Math.ceil(MEASURES.words(scope) / WORDS_PER_MINUTE)),
  rackrows: (scope) => scope.querySelectorAll(".rack-rows .rack-row").length,
  channels: (scope) => scope.querySelectorAll("[data-channel]").length,
  posters: (scope) => scope.querySelectorAll("[data-poster]").length,
  swatches: (scope) => scope.querySelectorAll(".swatch").length,
}

// Look up and run a measure by name; unknown measures read 0 (never throw).
export function measure(name, scope) {
  const fn = MEASURES[name]
  return fn ? fn(scope) : 0
}

// Write `target` into `el`, either instantly (reduced motion) or with the damped
// settle needle (one overshoot, never painted parked at zero). transforms nothing
// but text — no layout thrash.
export function settle(el, target, { reducedMotion = false } = {}) {
  if (reducedMotion) {
    el.textContent = target.toLocaleString("en")
    return
  }
  const duration = 460
  let start = null
  const frame = (now) => {
    if (start === null) start = now
    const p = Math.min(1, 0.12 + (now - start) / duration) // needle never parks at 0
    const damped = 1 - Math.exp(-5.2 * p) * Math.cos(9 * p) // one overshoot
    el.textContent = Math.max(0, Math.round(target * damped)).toLocaleString("en")
    if (p < 1) requestAnimationFrame(frame)
    else el.textContent = target.toLocaleString("en")
  }
  requestAnimationFrame(frame)
}
